/**
 * Combine plusieurs AbortSignals en un seul
 * @param {AbortSignal[]} signals - Tableau de signaux à combiner
 * @returns {AbortSignal} - Signal combiné
 */
export const combineAbortSignals = (signals) => {
  const controller = new AbortController();

  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      break;
    }

    signal.addEventListener('abort', () => {
      controller.abort();
    }, { once: true });
  }

  return controller.signal;
};

/**
 * Assistant fetch avec gestion du timeout via AbortController
 * @param {string} url - URL à appeler
 * @param {Object} options - Options passées à fetch
 * @param {number} timeout - Durée maximale en millisecondes (par défaut: 10000)
 * @returns {Promise} - Promise retournée par fetch ou rejet en cas de timeout
 */
export const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  // console.log(`Fetching ${url} with timeout ${timeout}ms`);
  // Crée un AbortController pour gérer le timeout
  const controller = new AbortController();
  const signal = controller.signal;

  // Planifie l'abort après le délai
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeout);

  try {
    // Fusionne le signal d'abort fourni dans options avec celui du timeout
    const fetchOptions = {
      ...options,
      signal: options.signal ?
        // S'il y a déjà un signal, on combine les deux
        combineAbortSignals([options.signal, signal]) :
        signal
    };

    const response = await fetch(url, fetchOptions);

    // Annule le timeout si la réponse arrive
    clearTimeout(timeoutId);

    return response;
  } catch (error) {
    // Annule le timeout en cas d'erreur
    clearTimeout(timeoutId);

    // Si l'erreur provient d'un abort (timeout), on normalise le message
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }

    // Rejette les autres erreurs telles quelles
    throw error;
  }
};

/**
 * Fetch avec retry et timeout
 * @param {string} url - URL à appeler
 * @param {Object} options - Options passées à fetch
 * @param {number} timeout - Timeout en millisecondes
 * @param {number} retries - Nombre de tentatives (par défaut: 3)
 * @param {number} retryDelay - Délai entre tentatives en ms (par défaut: 1000)
 * @returns {Promise} - Promise qui résout la réponse ou rejette l'erreur finale
 */
export const fetchWithTimeoutAndRetry = async (
  url,
  options = {},
  timeout = 10000,
  retries = 3,
  retryDelay = 1000
) => {
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchWithTimeout(url, options, timeout);
    } catch (error) {
      lastError = error;

      // Don't retry on certain errors (like 4xx client errors)
      if (error.response && error.response.status < 500) {
        throw error;
      }

      // Don't retry on the last attempt
      if (attempt === retries) {
        break;
      }

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, retryDelay));

      // Exponential backoff
      retryDelay *= 2;
    }
  }

  throw lastError;
};

export default fetchWithTimeout;
